import React, { useEffect, useState } from "react";
import "../App.css";
import logodark from "../karuncup-dark-1.svg";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { useSnackbar } from "notistack";
import payApi from "../utils/payApi";
import API from "../utils/api";

const InvoicePage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hasPaid, setHasPaid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leagueData, setLeagueData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/");
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("access_token");
        navigate("/");
        return;
      }
    } catch {
      navigate("/");
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${decoded.user_id}/`, { headers })
      .then((res) => setUserData(res.data))
      .catch((err) => {
        console.error(err);
        setError("خطا در بارگذاری اطلاعات کاربر.");
      });
  }, [navigate]);

  useEffect(() => {
    if (!userData) return;

    const token = localStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/leagues/${userData.league}/`, { headers })
      .then((res) => setLeagueData(res.data))
      .catch(() => setError("خطا در بارگذاری اطلاعات لیگ."));

    payApi
      .getPaymentStatus()
      .then((res) => {
        setHasPaid(res.data.has_paid);
        setTransactions(res.data.transactions || []);
      })
      .catch(() => setError("خطا در بررسی وضعیت پرداخت."));

    API.get(`/api/team-members/get/${encodeURIComponent(userData.email)}`, { headers })
      .then((r) => setTeamMembers(r.data || []))
      .catch(() => enqueueSnackbar("عدم موفقیت در بارگذاری اعضای تیم.", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [userData, enqueueSnackbar]);

  if (loading) return <div className="p-4">در حال بارگذاری...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!hasPaid) {
    navigate(-1);
    return null;
  }

  const successful = transactions.filter((tx) => tx.status);
  const totalAmount = successful.reduce((sum, tx) => sum + tx.amount, 0);
  const registrationFee = Number(leagueData?.registration_fee || 0);
  const participantCount = registrationFee ? Math.floor(totalAmount / registrationFee) : 0;
  const date = successful.length
    ? new Date(successful[0].created_at).toLocaleDateString("fa-IR")
    : new Date().toLocaleDateString("fa-IR");
  const siteTitle = "جشنواره و مسابقات فناوری‌های نوین در صنعت آب و برق کارون کاپ";

  const allMembers = [
    {
      id: "leader",
      firstname: userData.firstname,
      lastname: userData.lastname,
      expert: "سرپرست",
    },
    ...teamMembers,
  ];

  return (
    <div className="min-h-screen bg-white font-tahoma text-gray-800 p-10" dir="rtl">
      <div className="max-w-3xl mx-auto border border-black rounded-md p-4">
        <div className="grid grid-cols-5 items-center border-b border-gray-300 pb-4 mb-4">
          <img src={logodark} alt="Logo" className="w-20 p-2" />
          <div className="col-span-3 space-y-1 text-xs">
            <h1 className="font-bold">{siteTitle}</h1>
            <p>
              <strong>تلفن:</strong> 09909430460
            </p>
            <p>
              <strong>ایمیل:</strong> info@karuncup.ir
            </p>
          </div>
          <div className="col-span-1 space-y-2">
            <div className="text-center text-sm border border-black rounded-md p-2">تاریخ: {date}</div>
            <div className="text-center text-sm border border-black rounded-md p-2">
              <strong>شماره فاکتور‌ها:</strong>
              <div className="mt-1 text-xs leading-tight">
                {successful.map((tx) => (
                  <div key={tx.invoice_id}>{tx.invoice_id}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="text-lg leading-relaxed px-2 mb-6">
          گواهی می‌شود تیم <strong>{userData.teamfullname}</strong> به سرپرستی آقای/خانم <strong>{userData.firstname} {userData.lastname}</strong> مبلغ <strong>{totalAmount.toLocaleString()}</strong> تومان جهت ثبت‌نام <strong>{participantCount}</strong> عضو در لیگ <strong>{leagueData?.name}</strong> مطابق جدول زیر پرداخت نموده است.
          <br></br>
          مبلغ نهایی جهت حضور در لیگ برای تیم شما : <strong>{(allMembers.length * (leagueData?.registration_fee || 0)).toLocaleString()}</strong> تومان می باشد
          <br></br>
          این گواهی بدون تأیید دبیرخانه جشنواره فاقد اعتبار می‌باشد.
        </div>

        <div className="mb-6">
          <h2 className="font-semibold mb-2">اعضای تیم</h2>
          <table className="w-full text-sm border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-1 text-right">نام</th>
                <th className="border px-2 py-1 text-right">نام خانوادگی</th>
                <th className="border px-2 py-1 text-right">سمت</th>
              </tr>
            </thead>
            <tbody>
              {allMembers.map((member) => (
                <tr key={member.id}>
                  <td className="border px-2 py-1 text-right">{member.firstname}</td>
                  <td className="border px-2 py-1 text-right">{member.lastname}</td>
                  <td className="border px-2 py-1 text-right">{member.expert}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-8 text-left text-base">محل مهر و امضاء دبیرخانه جشنواره</div>

        <div className="border-2 border-black p-2 text-center font-bold">www.karuncup.ir</div>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={() => window.print()}
          className="bg-lime-600 hover:bg-lime-700 text-white px-6 py-2 rounded"
        >
          چاپ فاکتور
        </button>
      </div>
    </div>
  );
};

export default InvoicePage;
